/**
 * DocumentsPage — the Documents (DMS) workspace surface. Two layers:
 *   • a collection switch (My files / Invoices / Bills), and
 *   • per-collection content:
 *       - My files: KPI strip, type tabs, search + list/grid toggle, folders,
 *         and the `MasterListCard` / Dropbox-style grid of uploaded documents.
 *       - Invoices / Bills: read-only system documents, downloadable as PDFs.
 *
 * Built to the customers/invoices list design language (lead-summary, StatTile
 * KPIs, tabs, MasterListCard, cursor pagination).
 */
import * as React from "react";
import { Search, List as ListIcon, LayoutGrid, X } from "lucide-react";
import {
  Alert,
  TabsRoot,
  TabsList,
  TabsTrigger,
  ToggleGroupRoot,
  ToggleGroupItem,
} from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";
import { MasterListCard, ListPagination } from "../master-list/index.ts";
import { KpiStrip } from "./components/kpi-strip.tsx";
import { FolderBar, type FolderNode } from "./components/folder-bar.tsx";
import { DocumentRow } from "./components/document-row.tsx";
import { DocumentCard } from "./components/document-card.tsx";
import { CollectionSwitcher, type Collection } from "./components/collection-switcher.tsx";
import { InvoiceCollection, BillCollection } from "./components/record-collection.tsx";
import { UploadButton } from "./upload-button.tsx";
import { formatBytes } from "./lib/format.ts";

type Kind = "all" | "pdf" | "image" | "file" | "kb";
const PAGE_SIZE = 50;

export function DocumentsPage() {
  const [collection, setCollection] = React.useState<Collection>("files");

  return (
    <div className="space-y-[18px]">
      <Header collection={collection} />
      <CollectionSwitcher value={collection} onChange={setCollection} />
      {collection === "files" ? (
        <FilesView />
      ) : collection === "invoices" ? (
        <InvoiceCollection />
      ) : (
        <BillCollection />
      )}
    </div>
  );
}

function Header({ collection }: { collection: Collection }) {
  const summaryQ = trpc.documents.summary.useQuery({}, { enabled: collection === "files" });
  const summary = summaryQ.data;
  const sub =
    collection === "invoices"
      ? "Issued invoices — open or download the rendered PDF."
      : collection === "bills"
        ? "Recorded vendor bills — open or download the rendered PDF."
        : null;

  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-2xl font-bold leading-tight tracking-tight text-ink">Documents</h1>
        {collection === "files" ? (
          <p className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm text-muted">
            <span>
              <strong className="num font-bold text-ink">{summary?.total ?? 0}</strong> documents
            </span>
            <Dot />
            <span className="text-muted/80">
              <strong className="num">{summary?.folders ?? 0}</strong> folders
            </span>
            <Dot />
            <span className="text-muted/80">
              <strong className="num">{formatBytes(summary?.storageBytes ?? "0")}</strong> stored
            </span>
          </p>
        ) : (
          <p className="mt-1.5 text-sm text-muted">{sub}</p>
        )}
      </div>
    </header>
  );
}

function FilesView() {
  const utils = trpc.useUtils();
  const [folderId, setFolderId] = React.useState<string | null>(null);
  const [kind, setKind] = React.useState<Kind>("all");
  const [draftSearch, setDraftSearch] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [activeTags, setActiveTags] = React.useState<string[]>([]);
  const [view, setView] = React.useState<"list" | "grid">("list");

  const [cursor, setCursor] = React.useState<string | null>(null);
  const [prevStack, setPrevStack] = React.useState<(string | null)[]>([]);

  const tagsKey = activeTags.join(",");
  React.useEffect(() => {
    setCursor(null);
    setPrevStack([]);
  }, [folderId, kind, search, tagsKey]);

  const summaryQ = trpc.documents.summary.useQuery({});
  const foldersQ = trpc.documents.listFolders.useQuery({});
  const listQ = trpc.documents.list.useQuery({
    folderId,
    kind: kind === "all" || kind === "kb" ? undefined : kind,
    kbOnly: kind === "kb" ? true : undefined,
    search: search.trim() || undefined,
    tags: activeTags.length ? activeTags : undefined,
    cursor,
    limit: PAGE_SIZE,
  });

  const createFolder = trpc.documents.createFolder.useMutation({
    onSuccess: () => {
      void foldersQ.refetch();
      void summaryQ.refetch();
    },
  });
  const del = trpc.documents.delete.useMutation({
    onSuccess: () => {
      void listQ.refetch();
      void summaryQ.refetch();
    },
  });
  const setKb = trpc.documents.setKnowledgeBase.useMutation({
    onSuccess: () => void listQ.refetch(),
  });
  const onToggleKb = React.useCallback(
    (id: string, enabled: boolean) => setKb.mutate({ id, enabled }),
    [setKb],
  );

  const folders: FolderNode[] = foldersQ.data?.items ?? [];
  const items = listQ.data?.items ?? [];
  const summary = summaryQ.data;

  const availableTags = React.useMemo(() => {
    const set = new Set<string>();
    for (const d of items) d.tags.forEach((t) => set.add(t));
    return [...set].sort();
  }, [items]);

  const onUploaded = React.useCallback(() => {
    void listQ.refetch();
    void summaryQ.refetch();
  }, [listQ, summaryQ]);

  const onDownload = React.useCallback(
    async (id: string) => {
      const res = await utils.documents.getDownloadUrl.fetch({ id });
      window.open(res.url, "_blank", "noopener,noreferrer");
    },
    [utils],
  );
  const onDelete = React.useCallback((id: string) => del.mutate({ id }), [del]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(draftSearch);
  };

  const pageNumber = prevStack.length + 1;
  const showingFrom = items.length === 0 ? 0 : prevStack.length * PAGE_SIZE + 1;
  const showingTo = showingFrom === 0 ? 0 : showingFrom + items.length - 1;
  const hasNext = Boolean(listQ.data?.nextCursor);
  const hasPrev = prevStack.length > 0;
  const goNext = () => {
    if (!listQ.data?.nextCursor) return;
    setPrevStack((s) => [...s, cursor]);
    setCursor(listQ.data.nextCursor);
  };
  const goPrev = () => {
    setPrevStack((s) => {
      const next = [...s];
      const prev = next.pop() ?? null;
      setCursor(prev);
      return next;
    });
  };

  const tab = (id: Kind, label: string, count?: number) => (
    <TabsTrigger value={id} {...(count !== undefined ? { count } : {})}>
      {label}
    </TabsTrigger>
  );

  return (
    <div className="space-y-[18px]">
      {/* KPI strip */}
      {summary && (
        <KpiStrip
          total={summary.total}
          addedLast30d={summary.addedLast30d}
          storageBytes={summary.storageBytes}
          folders={summary.folders}
        />
      )}

      {/* Quick-filter tabs */}
      <TabsRoot value={kind} onValueChange={(v) => setKind(v as Kind)}>
        <TabsList aria-label="Filter by type">
          {tab("all", "All", summary?.total)}
          {tab("pdf", "PDFs", summary?.byKind.pdf)}
          {tab("image", "Images", summary?.byKind.image)}
          {tab("file", "Files", summary?.byKind.file)}
          {tab("kb", "Knowledge base")}
        </TabsList>
      </TabsRoot>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <form onSubmit={submitSearch} className="min-w-[260px] max-w-[380px] flex-1">
          <label className="flex h-9 items-center gap-2 rounded-sm border border-line bg-bg px-3 transition-colors focus-within:border-sky-600 focus-within:bg-paper focus-within:ring-2 focus-within:ring-sky-100">
            <Search size={14} strokeWidth={2} aria-hidden className="shrink-0 text-muted" />
            <input
              type="search"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              placeholder="Search documents…"
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-ink outline-0 placeholder:text-muted"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setDraftSearch("");
                  setSearch("");
                }}
                className="text-xs text-muted hover:text-ink"
              >
                clear
              </button>
            )}
          </label>
        </form>

        <div className="hidden flex-1 sm:block" />

        <ToggleGroupRoot
          type="single"
          value={view}
          onValueChange={(v) => v && setView(v as "list" | "grid")}
          className="hidden sm:inline-flex"
        >
          <ToggleGroupItem value="list" aria-label="List view">
            <ListIcon size={14} aria-hidden />
          </ToggleGroupItem>
          <ToggleGroupItem value="grid" aria-label="Grid view">
            <LayoutGrid size={14} aria-hidden />
          </ToggleGroupItem>
        </ToggleGroupRoot>

        <UploadButton folderId={folderId} onUploaded={onUploaded} />
      </div>

      {/* Active tag filters */}
      {availableTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium uppercase tracking-wider text-muted">Tags</span>
          {availableTags.map((t) => {
            const active = activeTags.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => setActiveTags((p) => (active ? p.filter((x) => x !== t) : [...p, t]))}
                aria-pressed={active}
                className={
                  "inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-xs font-medium " +
                  (active ? "border-sky-500 bg-sky-50 text-sky-700" : "border-line bg-paper text-ink-2 hover:bg-bg")
                }
              >
                {t}
                {active && <X size={11} aria-hidden />}
              </button>
            );
          })}
        </div>
      )}

      {/* Folder navigation (hidden in the KB view — it spans all folders) */}
      {kind !== "kb" && (
        <FolderBar
          folders={folders}
          currentFolderId={folderId}
          onNavigate={setFolderId}
          onCreate={(name, parentId) => createFolder.mutate({ name, parentId })}
          creating={createFolder.isPending}
        />
      )}

      {createFolder.error && <Alert intent="bad" description={createFolder.error.message} />}
      {del.error && <Alert intent="bad" description={del.error.message} />}

      {/* Body */}
      {listQ.error ? (
        <Alert intent="bad" title="Couldn't load documents" description={listQ.error.message} />
      ) : listQ.isLoading ? (
        <p className="text-sm text-muted">Loading documents…</p>
      ) : items.length === 0 ? (
        <EmptyState hasFilter={Boolean(search.trim() || activeTags.length || kind !== "all")} />
      ) : view === "grid" ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {items.map((d) => (
            <DocumentCard
              key={d.id}
              document={d}
              onDownload={(id) => void onDownload(id)}
              onDelete={onDelete}
              onToggleKb={onToggleKb}
            />
          ))}
        </div>
      ) : (
        <MasterListCard
          header={
            <div className="grid grid-cols-[44px_minmax(0,1.6fr)_minmax(0,1fr)_96px_120px_auto] items-center gap-4 px-5 py-2.5">
              <span aria-hidden />
              <span>Name</span>
              <span>Tags</span>
              <span className="text-right">Type</span>
              <span className="text-right">Uploaded</span>
              <span aria-hidden />
            </div>
          }
          footer={[
            { label: "Showing", value: items.length },
            { label: "Storage", value: formatBytes(String(items.reduce((s, d) => s + Number(d.sizeBytes), 0))) },
          ]}
          pagination={
            hasPrev || hasNext ? (
              <ListPagination
                pageNumber={pageNumber}
                showingFrom={showingFrom}
                showingTo={showingTo}
                sortLabel="newest first"
                hasPrev={hasPrev}
                hasNext={hasNext}
                onPrev={goPrev}
                onNext={goNext}
              />
            ) : undefined
          }
        >
          {items.map((d) => (
            <DocumentRow
              key={d.id}
              document={d}
              onDownload={(id) => void onDownload(id)}
              onDelete={onDelete}
              onToggleKb={onToggleKb}
            />
          ))}
        </MasterListCard>
      )}
    </div>
  );
}

function Dot() {
  return <span aria-hidden className="h-[3px] w-[3px] shrink-0 rounded-full bg-line-2" />;
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  return (
    <div className="rounded-md border border-dashed border-line bg-paper px-6 py-14 text-center">
      <LayoutGrid size={26} className="mx-auto text-line-2" aria-hidden />
      <p className="mt-3 text-base font-semibold text-ink">
        {hasFilter ? "No documents match your filters." : "No documents here yet."}
      </p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted">
        {hasFilter
          ? "Try a different search, type, or tag."
          : "Upload PDFs, images, or office files to start your repository."}
      </p>
    </div>
  );
}
